/**
 * Mirrors the JSON from API.Services.DxfExtractionDetailsJsonBuilder (camelCase from System.Text.Json).
 */
export interface DxfPolylineVolumeLine {
  sequenceIndex: number;
  planAreaDrawingUnits: number;
  /** Axis-aligned bbox extent along X in drawing units. UI Length/Width use max/min of X and Y extents. */
  planExtentXDrawingUnits: number;
  /** Axis-aligned bbox extent along Y in drawing units. UI Length/Width use max/min of X and Y extents. */
  planExtentYDrawingUnits: number;
  thicknessDrawingUnits: number;
  volumeDrawingUnits: number;
  planAreaSquareMeters: number;
  volumeCubicMeters: number;
}

export interface DxfMappingSummary {
  extractedCount: number;
  matchedCount: number;
  unmatchedCount: number;
  estimateLineCount: number;
  unmatchedLayerCodes: string[];
}

export interface DxfRawLayer {
  dxfCode: string;
  blockInsertCount: number;
  polylineCount: number;
  lineLengthSum: number;
  polylineVolumeSum: number;
  legacyQuantity: number;
  polylineVolumeBreakdown: DxfPolylineVolumeLine[];
}

export interface DxfMappedLine {
  costItemId: number;
  itemCode: string;
  unitId: number;
  quantity: number;
  selectedCostItemSupplierId?: number | null;
  sourceDxfCode: string;
}

export interface DxfExtractionDetailsPayload {
  fileName: string;
  linearScaleToMeter: number;
  mappingSummary: DxfMappingSummary;
  rawLayers: DxfRawLayer[];
  mergedLines: DxfMappedLine[];
}

function num(v: unknown, fallback = 0): number {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isNaN(n) ? fallback : n;
  }
  return fallback;
}

function str(v: unknown): string {
  return v == null ? '' : String(v);
}

/** Stored `detailsJson` uses camelCase (see DxfExtractionDetailsJsonBuilder). Accept PascalCase for older rows. */
export function parseDxfExtractionDetailsJson(raw: string): DxfExtractionDetailsPayload {
  const j = JSON.parse(raw) as Record<string, unknown>;
  const g = (k: string, alt?: string) => j[k] ?? (alt ? j[alt] : undefined);

  const mapPolyline = (row: Record<string, unknown>): DxfPolylineVolumeLine => {
    const rawPx = row.PlanExtentXDrawingUnits ?? row.planExtentXDrawingUnits;
    const rawPy = row.PlanExtentYDrawingUnits ?? row.planExtentYDrawingUnits;
    return {
    sequenceIndex: num(row.SequenceIndex ?? row.sequenceIndex),
    planAreaDrawingUnits: num(row.PlanAreaDrawingUnits ?? row.planAreaDrawingUnits),
    /** Absent in older stored `detailsJson` — use NaN so UI shows "—", not misleading 0. */
    planExtentXDrawingUnits: rawPx === undefined ? Number.NaN : num(rawPx),
    planExtentYDrawingUnits: rawPy === undefined ? Number.NaN : num(rawPy),
    thicknessDrawingUnits: num(row.ThicknessDrawingUnits ?? row.thicknessDrawingUnits),
    volumeDrawingUnits: num(row.VolumeDrawingUnits ?? row.volumeDrawingUnits),
    planAreaSquareMeters: num(row.PlanAreaSquareMeters ?? row.planAreaSquareMeters),
    volumeCubicMeters: num(row.VolumeCubicMeters ?? row.volumeCubicMeters),
  };
  };

  const mapRawLayer = (row: Record<string, unknown>): DxfRawLayer => {
    const breakdown = row.PolylineVolumeBreakdown ?? row.polylineVolumeBreakdown;
    const arr = Array.isArray(breakdown) ? breakdown : [];
    return {
      dxfCode: str(row.DxfCode ?? row.dxfCode),
      blockInsertCount: num(row.BlockInsertCount ?? row.blockInsertCount),
      polylineCount: num(row.PolylineCount ?? row.polylineCount),
      lineLengthSum: num(row.LineLengthSum ?? row.lineLengthSum),
      polylineVolumeSum: num(row.PolylineVolumeSum ?? row.polylineVolumeSum),
      legacyQuantity: num(row.LegacyQuantity ?? row.legacyQuantity),
      polylineVolumeBreakdown: arr.map((x) => mapPolyline(x as Record<string, unknown>)),
    };
  };

  const mapMappedLine = (row: Record<string, unknown>): DxfMappedLine => {
    const sid = row.SelectedCostItemSupplierId ?? row.selectedCostItemSupplierId;
    return {
      costItemId: num(row.CostItemId ?? row.costItemId),
      itemCode: str(row.ItemCode ?? row.itemCode),
      unitId: num(row.UnitId ?? row.unitId),
      quantity: num(row.Quantity ?? row.quantity),
      selectedCostItemSupplierId: sid == null ? null : num(sid),
      sourceDxfCode: str(row.SourceDxfCode ?? row.sourceDxfCode),
    };
  };

  const ms = (g('MappingSummary', 'mappingSummary') ?? {}) as Record<string, unknown>;
  const unmatchedRaw = ms.UnmatchedLayerCodes ?? ms.unmatchedLayerCodes;
  const mappingSummary: DxfMappingSummary = {
    extractedCount: num(ms.ExtractedCount ?? ms.extractedCount),
    matchedCount: num(ms.MatchedCount ?? ms.matchedCount),
    unmatchedCount: num(ms.UnmatchedCount ?? ms.unmatchedCount),
    estimateLineCount: num(ms.EstimateLineCount ?? ms.estimateLineCount),
    unmatchedLayerCodes: Array.isArray(unmatchedRaw)
      ? unmatchedRaw.map((x: unknown) => str(x))
      : [],
  };

  const rawLayersRaw = g('RawLayers', 'rawLayers');
  const mergedRaw = g('MergedLines', 'mergedLines');

  return {
    fileName: str(g('FileName', 'fileName')),
    linearScaleToMeter: num(g('LinearScaleToMeter', 'linearScaleToMeter'), 1),
    mappingSummary,
    rawLayers: Array.isArray(rawLayersRaw)
      ? rawLayersRaw.map((x) => mapRawLayer(x as Record<string, unknown>))
      : [],
    mergedLines: Array.isArray(mergedRaw)
      ? mergedRaw.map((x) => mapMappedLine(x as Record<string, unknown>))
      : [],
  };
}
